// Svelte 5 no longer needs svelte/store imports for basic reactivity
import project from '@app/external/cc-lib/dist/classes/project.js';
import loadMedia from '@cclib/functions/projects/loadMedia.js';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '@app/store/envStore.svelte.js';
import { uiState } from '@app/store/uiStore.svelte.js';
import {
  updateDuration,
  updatePlayingStatus,
} from '@app/store/playerStore.svelte.js';
import {
  generatePeaks,
  peaksFileExists,
} from '@app/lib/media/generatePeaks.js';

// Create reactive state for the project
let ProjectData = JSON.parse(JSON.stringify(new project()));
export const projectState = $state(ProjectData);

function getMediaIdentity(mediaObj) {
  return mediaObj?.localPath || mediaObj?.path || mediaObj?.name || '';
}

function createMediaVersionSnapshot(mediaObj, name, versionId = uuidv4()) {
  return {
    versionId,
    versionName: name || mediaObj?.name || 'Untitled',
    ...JSON.parse(JSON.stringify(mediaObj || {})),
  };
}

function syncActiveVersionFromProjectMedia() {
  if (!projectState.activeMediaVersion) {
    return;
  }

  const index = projectState.mediaVersions.findIndex(
    (version) => version.versionId === projectState.activeMediaVersion,
  );

  if (index < 0) {
    return;
  }

  const { versionId, versionName } = projectState.mediaVersions[index];
  projectState.mediaVersions[index] = createMediaVersionSnapshot(
    projectState.media,
    versionName,
    versionId,
  );
}

function getPlaybackSource(mediaObj) {
  if (!mediaObj) {
    return null;
  }

  if (mediaObj.storage === 'Proxy RT' && mediaObj.path) {
    return mediaObj.path;
  }

  return environment.electron ? mediaObj.localPath : mediaObj.path;
}

function getPlaybackMediaType(mediaObj) {
  if (!mediaObj?.type) {
    return 'video/mp4';
  }

  return mediaObj.type === 'video/quicktime' ? 'video/mp4' : mediaObj.type;
}

async function applyMediaPeaks(mediaObj, versionSnapshot = null) {
  if (!environment.electron || !mediaObj?.localPath) {
    return;
  }

  if (mediaObj.peaksPath && peaksFileExists(mediaObj.peaksPath)) {
    projectState.media.peaksPath = mediaObj.peaksPath;
    projectState.media.useFallback = false;
    return;
  }

  try {
    const peaksPath = await generatePeaks(mediaObj);
    projectState.media.peaksPath = peaksPath;
    projectState.media.useFallback = false;

    if (versionSnapshot) {
      versionSnapshot.peaksPath = peaksPath;
    }
  } catch (err) {
    console.error('Failed to generate waveform peaks:', err);
    projectState.media.peaksPath = '';
    projectState.media.useFallback = true;
  }
}

function setPlayerSource(mediaObj) {
  if (!window.player || !mediaObj) {
    return;
  }

  const src = getPlaybackSource(mediaObj);
  if (!src) {
    window.player.src = '';
    return;
  }

  window.player.src = {
    src,
    type: getPlaybackMediaType(mediaObj),
  };
}

/**
 * Updates the project ID with a new ID
 * @param {string} newId - The new ID to assign to the project
 */
export function updateProjectId(newId) {
  if (!newId) {
    console.error('Invalid project ID provided to updateProjectId');
    return;
  }
  projectState.id = newId;
}

/**
 * Resets the project state with new properties
 * @param {object} projectProps - The properties to initialize the project with
 */
export function resetProjectState(projectProps = {}) {
  const newProjectData = JSON.parse(JSON.stringify(new project(projectProps)));
  Object.assign(projectState, newProjectData);
}

/**
 * Updates the project's media property using the loadMedia function
 * @param {object} mediaInfo - The media information to update the project with
 */
export function updateProjectMedia(mediaInfo) {
  if (!mediaInfo) {
    console.error('Invalid media info provided to updateProjectMedia');
    return;
  }

  const tempProject = loadMedia({ ...projectState }, mediaInfo);
  projectState.media = tempProject.media;
}

/**
 * Updates the selected event group index in the project state
 * @param {number} evgIndex - The index of the event group to select
 */
export function updateSelectedEventGroup(evgIndex) {
  projectState.selected = Number(evgIndex);
}

/**
 * Adds or updates a media version snapshot in the project.
 * @param {object} mediaObj
 * @param {string} [name]
 */
export function addMediaVersion(mediaObj, name) {
  if (!mediaObj || !getMediaIdentity(mediaObj)) {
    return;
  }

  if (!Array.isArray(projectState.mediaVersions)) {
    projectState.mediaVersions = [];
  }

  const identity = getMediaIdentity(mediaObj);
  const existingIndex = projectState.mediaVersions.findIndex(
    (version) => getMediaIdentity(version) === identity,
  );

  let snapshot;

  if (existingIndex >= 0) {
    const existingVersion = projectState.mediaVersions[existingIndex];
    snapshot = createMediaVersionSnapshot(
      mediaObj,
      name || existingVersion.versionName,
      existingVersion.versionId,
    );
    projectState.mediaVersions[existingIndex] = snapshot;
  } else {
    snapshot = createMediaVersionSnapshot(mediaObj, name);
    projectState.mediaVersions = [...projectState.mediaVersions, snapshot];
  }

  projectState.activeMediaVersion = snapshot.versionId;
}

/**
 * Removes a media version from the project.
 * @param {string} versionId
 */
export async function removeMediaVersion(versionId) {
  const index = projectState.mediaVersions.findIndex(
    (version) => version.versionId === versionId,
  );

  if (index < 0) {
    return;
  }

  const wasActive = projectState.activeMediaVersion === versionId;
  projectState.mediaVersions = projectState.mediaVersions.filter(
    (version) => version.versionId !== versionId,
  );

  if (!wasActive) {
    return;
  }

  if (projectState.mediaVersions.length === 0) {
    projectState.activeMediaVersion = false;
    projectState.media = {};
    updatePlayingStatus(false);
    updateDuration(false);
    uiState.timeline = false;

    if (window.player) {
      window.player.src = '';
    }

    return;
  }

  const nextIndex = Math.min(index, projectState.mediaVersions.length - 1);
  await switchMediaVersion(projectState.mediaVersions[nextIndex].versionId);
}

/**
 * Reorders media versions in the project list.
 * @param {number} fromIndex
 * @param {number} toIndex
 */
export function reorderMediaVersions(fromIndex, toIndex) {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= projectState.mediaVersions.length ||
    toIndex >= projectState.mediaVersions.length
  ) {
    return;
  }

  const versions = [...projectState.mediaVersions];
  const [movedVersion] = versions.splice(fromIndex, 1);
  versions.splice(toIndex, 0, movedVersion);
  projectState.mediaVersions = versions;
}

/**
 * Renames a media version.
 * @param {string} versionId
 * @param {string} name
 */
export function renameMediaVersion(versionId, name) {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    return;
  }

  const index = projectState.mediaVersions.findIndex(
    (version) => version.versionId === versionId,
  );

  if (index < 0) {
    return;
  }

  projectState.mediaVersions[index].versionName = trimmedName;
}

/**
 * Switches the active media version for the project.
 * @param {string} versionId
 */
export async function switchMediaVersion(versionId) {
  if (projectState.activeMediaVersion === versionId) {
    return;
  }

  syncActiveVersionFromProjectMedia();

  const version = projectState.mediaVersions.find(
    (entry) => entry.versionId === versionId,
  );

  if (!version) {
    return;
  }

  const { versionId: activeId, versionName, ...mediaFields } = version;

  updatePlayingStatus(false);
  updateDuration(false);
  uiState.timeline = false;

  if (window.player) {
    window.player.src = '';
  }

  projectState.media = JSON.parse(JSON.stringify(mediaFields));
  projectState.activeMediaVersion = activeId;

  setPlayerSource(projectState.media);
  await applyMediaPeaks(projectState.media, version);

  syncActiveVersionFromProjectMedia();
  uiState.timeline = true;
}
